import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Play, Pause, ArrowLeft, Calendar, User, BookOpen, Heart, Sparkles, ChevronRight, Film, PenLine } from 'lucide-react';
import MarkdownRenderer from './MarkdownRenderer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Initial seed blog posts - these will be migrated to database
const SEED_BLOG_POSTS = [
  {
    id: 'post-1',
    title: 'The Journey Begins: Finding Our Way Back to Connection',
    author: 'Tony Macasaet',
    author_role: 'Founder, The Belonging Sessions',
    created_at: '2024-12-01',
    featured: true,
    category: 'Reflections',
    read_time: '8 min read',
    image_url: 'https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=800',
    excerpt: 'How a simple question from my daughter changed everything I thought I knew about communication.',
    content: `In the autumn of 2023, my daughter Gail asked me a question that stopped me in my tracks: "Dad, why do we always talk past each other?"

She was fourteen, brilliant beyond her years, and she had noticed something I had spent decades avoiding—the wall between us wasn't made of disagreement, it was made of disconnection.

## The Moment of Awakening

I had spent thirty years in corporate leadership, mastering the art of persuasion, negotiation, and what I thought was "effective communication." But my daughter saw through all of it. She didn't need me to be right. She needed me to be *present*.

That question led me to Marshall Rosenberg's work on Nonviolent Communication, and eventually to create The Belonging Sessions—a space where families could learn to speak the language of the heart.

## What I've Learned

Connection isn't about having the perfect words. It's about:
- **Presence** - Being fully with another person, not planning your next response
- **Curiosity** - Wondering what's alive in them, not judging what's wrong
- **Vulnerability** - Sharing your needs, not hiding behind your demands

## The Invitation

If you're reading this, perhaps you too have felt that wall. Perhaps you've wondered why conversations with those you love most sometimes feel like speaking different languages.

You're not alone. And there is a path home.

*Welcome to The Belonging Sessions.*`
  },
  {
    id: 'post-2', 
    title: 'When Words Become Walls: Understanding Jackal Language',
    author: 'Tony Macasaet',
    author_role: 'Founder, The Belonging Sessions',
    created_at: '2024-11-15',
    featured: false,
    category: 'NVC Foundations',
    read_time: '6 min read',
    image_url: 'https://images.unsplash.com/photo-1516534775068-ba3e7458af70?w=800',
    excerpt: 'Recognizing the language patterns that disconnect us from ourselves and others.',
    content: `Marshall Rosenberg called it "Jackal language"—not because jackals are bad creatures, but because this way of speaking comes from a place of self-protection, fear, and disconnection.

## The Four D's of Disconnection

In our sessions, we explore what I call the Four D's—patterns of speech that build walls instead of bridges:

### 1. Diagnosis
"You're so selfish." "She's manipulative." "He's lazy."

When we diagnose others, we stop seeing them as complex human beings with needs and start seeing them as labels to manage or avoid.

### 2. Denial of Responsibility
"I had to yell at them—they made me so angry." "I couldn't help it."

This language removes our agency and places it in the hands of others, leaving us powerless and resentful.

### 3. Demands
"You need to..." "You should..." "You have to..."

Demands backed by punishment or reward create compliance, not connection.

### 4. Deserve-Oriented Thinking
"They got what they deserved." "I don't deserve this."

This thinking puts us in the position of judge, determining worth and punishment.

## The Shift to Giraffe Language

The antidote isn't perfection—it's awareness. When we catch ourselves in Jackal language, we can pause and ask:

*"What am I feeling right now?"*
*"What need of mine isn't being met?"*
*"What could I request that would help?"*

This is the beginning of transformation.`
  },
  {
    id: 'post-3',
    title: 'A Mother\'s Gift: How My Mom Taught Me Empathy Without Words',
    author: 'Tony Macasaet',
    author_role: 'Founder, The Belonging Sessions',
    created_at: '2024-10-28',
    featured: false,
    category: 'Personal Stories',
    read_time: '5 min read',
    image_url: 'https://images.unsplash.com/photo-1491438590914-bc09fcaaf77a?w=800',
    excerpt: 'A tribute to the woman who showed me that listening is the greatest act of love.',
    content: `My mother never read Marshall Rosenberg. She never attended a workshop on Nonviolent Communication. But she understood something that I've spent years trying to articulate.

## The Kitchen Table Sessions

Every Sunday evening, she would sit at our kitchen table with a cup of tea, and she would *just listen*. 

Not fix. Not advise. Not judge. Just listen.

When I came home frustrated about a friend, she didn't say, "Well, you should..." Instead, she asked, "That sounds hard. What was that like for you?"

When I failed a test, she didn't lecture about studying harder. She asked, "What do you need right now?"

## The Wisdom She Left Behind

She passed away three years ago, but her presence lives in every session we hold. Her questions echo in the practices we teach:

- **"What's alive in you right now?"** - Her way of asking about feelings
- **"What would help?"** - Her way of discovering needs
- **"How can I be with you in this?"** - Her way of offering presence

## The Belonging She Created

My mother created belonging not through grand gestures, but through consistent presence. She showed me that empathy isn't a technique—it's a way of being.

When we sit in circle at The Belonging Sessions, I feel her there. In every moment of genuine listening, she lives on.

*To my mother: Thank you for teaching me that the greatest gift we can give is our undivided attention.*`
  }
];

export default function LoreExperience() {
  const navigate = useNavigate();
  const [userId] = useState(() => localStorage.getItem('workbook_user_id'));
  const [blogPosts, setBlogPosts] = useState(SEED_BLOG_POSTS);
  const [selectedPost, setSelectedPost] = useState(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [activeTab, setActiveTab] = useState('stories');

  useEffect(() => {
    fetchBlogPosts();
  }, []);

  const fetchBlogPosts = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/lore/blog-posts`);
      if (response.ok) {
        const data = await response.json();
        if (data.posts && data.posts.length > 0) {
          setBlogPosts(data.posts);
        }
      }
    } catch (error) {
      console.log('Using seed blog posts');
    }
  };

  // Full article view
  if (selectedPost) {
    return (
      <div className="min-h-screen bg-black text-white">
        {/* Cinematic Header */}
        <header className="sticky top-0 z-50 bg-gradient-to-b from-black via-black/95 to-transparent">
          <div className="max-w-5xl mx-auto px-6 py-6 flex justify-between items-center">
            <button 
              onClick={() => setSelectedPost(null)}
              className="flex items-center gap-2 text-amber-500/80 hover:text-amber-400 transition-colors text-sm tracking-wide"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to LORE
            </button>
            <div className="flex items-center gap-3">
              <span className="text-stone-600 text-xs tracking-[0.2em] uppercase">{selectedPost.category}</span>
            </div>
          </div>
        </header>

        {/* Article Hero */}
        <div className="relative h-[50vh] min-h-[400px] overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent z-10"></div>
          {selectedPost.image_url && (
            <img 
              src={selectedPost.image_url} 
              alt={selectedPost.title}
              className="absolute inset-0 w-full h-full object-cover opacity-40"
            />
          )}
          <div className="absolute inset-0 z-20 flex items-end">
            <div className="max-w-4xl mx-auto px-6 pb-16 w-full">
              {selectedPost.featured && (
                <div className="flex items-center gap-2 text-amber-500 mb-4">
                  <Sparkles className="w-4 h-4" />
                  <span className="text-xs tracking-[0.3em] uppercase">Featured</span>
                </div>
              )}
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-light leading-tight mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                {selectedPost.title}
              </h1>
              <div className="flex items-center gap-6 text-stone-400 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center text-white font-semibold">
                    {selectedPost.author.charAt(0)}
                  </div>
                  <div>
                    <p className="text-white font-medium">{selectedPost.author}</p>
                    <p className="text-stone-500 text-xs">{selectedPost.author_role}</p>
                  </div>
                </div>
                <span className="text-stone-600">·</span>
                <span>{new Date(selectedPost.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                <span className="text-stone-600">·</span>
                <span>{selectedPost.read_time}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Article Content */}
        <article className="max-w-3xl mx-auto px-6 py-16">
          <div className="prose prose-invert prose-lg max-w-none prose-headings:font-light prose-headings:tracking-wide prose-p:text-stone-300 prose-p:leading-relaxed prose-strong:text-white prose-li:text-stone-300" style={{ fontFamily: 'Georgia, serif' }}>
            <MarkdownRenderer content={selectedPost.content} />
          </div>

          {/* Author Card */}
          <div className="mt-20 pt-12 border-t border-stone-800">
            <div className="flex items-start gap-6">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center text-white text-2xl font-light flex-shrink-0">
                {selectedPost.author.charAt(0)}
              </div>
              <div>
                <p className="text-xs text-amber-500 tracking-[0.2em] uppercase mb-2">Written by</p>
                <h3 className="text-xl text-white font-light mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>{selectedPost.author}</h3>
                <p className="text-stone-500 text-sm mb-4">{selectedPost.author_role}</p>
                <p className="text-stone-400 text-sm leading-relaxed max-w-lg">
                  Sharing stories of belonging, connection, and the transformative power of compassionate communication.
                </p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-16 p-8 rounded-lg bg-gradient-to-br from-amber-900/20 to-stone-900/40 border border-amber-500/20 text-center">
            <h3 className="text-2xl text-white font-light mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Continue Your Journey
            </h3>
            <p className="text-stone-400 mb-6 max-w-md mx-auto">
              Join The Belonging Sessions and learn the language of compassionate connection.
            </p>
            <Button 
              onClick={() => navigate('/')}
              className="bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 text-white px-8 py-3 rounded-none"
            >
              Reserve Your Seat
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </article>

        {/* Footer */}
        <footer className="border-t border-stone-900 py-12 mt-16">
          <div className="max-w-5xl mx-auto px-6 text-center">
            <p className="text-stone-600 text-sm">
              © 2025 The Belonging Sessions • Stories of Connection
            </p>
          </div>
        </footer>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      
      {/* Cinematic Hero with Video Placeholder */}
      <header className="relative overflow-hidden" style={{ minHeight: '70vh' }}>
        {/* Dark gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black via-black/80 to-black z-10"></div>
        
        {/* Animated background texture */}
        <div className="absolute inset-0 opacity-20 z-0">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(212, 175, 55, 0.3) 1px, transparent 0)',
            backgroundSize: '50px 50px'
          }}></div>
        </div>

        {/* Video Hero Section */}
        <div className="relative z-20 max-w-6xl mx-auto px-6 pt-8">
          {/* Navigation */}
          <nav className="flex justify-between items-center mb-12">
            <button 
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-stone-500 hover:text-amber-500 transition-colors text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              Home
            </button>
            <div className="flex items-center gap-6">
              <button 
                onClick={() => navigate('/belonging-ebook')}
                className="text-stone-500 hover:text-amber-500 transition-colors text-sm"
              >
                Practice eBook
              </button>
              {userId && (
                <button 
                  onClick={() => navigate('/dashboard')}
                  className="text-stone-500 hover:text-amber-500 transition-colors text-sm"
                >
                  My Dashboard
                </button>
              )}
            </div>
          </nav>

          {/* Featured Video Placeholder */}
          <div className="relative rounded-lg overflow-hidden bg-gradient-to-br from-stone-900 to-black border border-stone-800 mb-16" style={{ aspectRatio: '21/9' }}>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center mx-auto mb-6 cursor-pointer hover:scale-110 transition-transform shadow-2xl shadow-amber-500/30">
                  <Play className="w-8 h-8 text-white ml-1" />
                </div>
                <p className="text-amber-500 text-xs tracking-[0.3em] uppercase mb-2">Featured Video</p>
                <h2 className="text-2xl md:text-3xl text-white font-light" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Welcome to The Belonging Sessions
                </h2>
                <p className="text-stone-500 text-sm mt-2">Coming Soon</p>
              </div>
            </div>
            {/* Decorative corners */}
            <div className="absolute top-4 left-4 w-8 h-8 border-l-2 border-t-2 border-amber-500/30"></div>
            <div className="absolute top-4 right-4 w-8 h-8 border-r-2 border-t-2 border-amber-500/30"></div>
            <div className="absolute bottom-4 left-4 w-8 h-8 border-l-2 border-b-2 border-amber-500/30"></div>
            <div className="absolute bottom-4 right-4 w-8 h-8 border-r-2 border-b-2 border-amber-500/30"></div>
          </div>

          {/* LORE Title */}
          <div className="text-center pb-16">
            <p className="text-amber-500 text-xs tracking-[0.4em] uppercase mb-4">The Archive of</p>
            <h1 className="text-6xl md:text-8xl font-light tracking-[0.2em] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              LORE
            </h1>
            <p className="text-stone-500 text-sm tracking-[0.2em] uppercase mb-6">
              Learning • Observations • Reflections • Evolution
            </p>
            <p className="text-stone-400 text-lg font-light max-w-2xl mx-auto leading-relaxed">
              Stories of belonging, wisdom from the journey, and the transformative power of compassionate communication.
            </p>
          </div>
        </div>
      </header>

      {/* Content Tabs */}
      <nav className="sticky top-0 z-40 bg-black/95 backdrop-blur-sm border-b border-stone-900">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex justify-center">
            {[
              { id: 'stories', label: 'Stories & Essays', icon: BookOpen },
              { id: 'videos', label: 'Video Library', icon: Film },
              { id: 'personal', label: 'My Journey', icon: Heart, requiresAuth: true },
            ].map(tab => (
              (!tab.requiresAuth || userId) && (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-8 py-5 text-sm tracking-[0.1em] transition-all border-b-2 flex items-center gap-2 ${
                    activeTab === tab.id 
                      ? 'text-amber-500 border-amber-500' 
                      : 'text-stone-600 border-transparent hover:text-stone-400'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              )
            ))}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-16">
        
        {/* Stories & Essays Tab */}
        {activeTab === 'stories' && (
          <div className="space-y-16">
            {/* Featured Post */}
            {blogPosts.filter(p => p.featured)[0] && (
              <section>
                <p className="text-amber-500 text-xs tracking-[0.3em] uppercase mb-8 flex items-center gap-2">
                  <Sparkles className="w-4 h-4" />
                  Featured Story
                </p>
                <article 
                  onClick={() => setSelectedPost(blogPosts.filter(p => p.featured)[0])}
                  className="group cursor-pointer"
                >
                  <div className="grid md:grid-cols-2 gap-8 items-center">
                    <div className="relative aspect-[4/3] overflow-hidden rounded-lg">
                      <img 
                        src={blogPosts.filter(p => p.featured)[0].image_url}
                        alt={blogPosts.filter(p => p.featured)[0].title}
                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    </div>
                    <div>
                      <p className="text-stone-600 text-xs tracking-[0.2em] uppercase mb-4">{blogPosts.filter(p => p.featured)[0].category}</p>
                      <h2 className="text-3xl md:text-4xl font-light text-white mb-4 group-hover:text-amber-500 transition-colors leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
                        {blogPosts.filter(p => p.featured)[0].title}
                      </h2>
                      <p className="text-stone-400 leading-relaxed mb-6">
                        {blogPosts.filter(p => p.featured)[0].excerpt}
                      </p>
                      <div className="flex items-center gap-4 text-sm text-stone-500">
                        <span>{blogPosts.filter(p => p.featured)[0].author}</span>
                        <span className="w-1 h-1 rounded-full bg-stone-700"></span>
                        <span>{blogPosts.filter(p => p.featured)[0].read_time}</span>
                      </div>
                    </div>
                  </div>
                </article>
              </section>
            )}

            {/* All Posts Grid */}
            <section>
              <h3 className="text-stone-600 text-xs tracking-[0.3em] uppercase mb-8">All Stories</h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {blogPosts.filter(p => !p.featured).map(post => (
                  <article 
                    key={post.id}
                    onClick={() => setSelectedPost(post)}
                    className="group cursor-pointer"
                  >
                    <div className="relative aspect-[3/2] overflow-hidden rounded-lg mb-4">
                      <img 
                        src={post.image_url}
                        alt={post.title}
                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    </div>
                    <p className="text-stone-600 text-xs tracking-[0.2em] uppercase mb-2">{post.category}</p>
                    <h3 className="text-xl font-light text-white mb-3 group-hover:text-amber-500 transition-colors leading-snug" style={{ fontFamily: 'Playfair Display, serif' }}>
                      {post.title}
                    </h3>
                    <p className="text-stone-500 text-sm line-clamp-2 mb-4">{post.excerpt}</p>
                    <div className="flex items-center gap-3 text-xs text-stone-600">
                      <span>{post.author}</span>
                      <span className="w-1 h-1 rounded-full bg-stone-700"></span>
                      <span>{post.read_time}</span>
                    </div>
                  </article>
                ))}
              </div>
            </section>

            {/* Write Your Story CTA (for logged-in users) */}
            {userId && (
              <section className="mt-20 p-12 rounded-lg bg-gradient-to-br from-stone-900 to-black border border-stone-800 text-center">
                <PenLine className="w-8 h-8 text-amber-500 mx-auto mb-4" />
                <h3 className="text-2xl text-white font-light mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Share Your Story
                </h3>
                <p className="text-stone-400 mb-6 max-w-md mx-auto">
                  Have a story of connection, transformation, or belonging? Write it in your dashboard and submit it for the LORE library.
                </p>
                <Button 
                  onClick={() => navigate('/dashboard')}
                  className="bg-transparent border border-amber-500 text-amber-500 hover:bg-amber-500 hover:text-black px-8 py-3 rounded-none transition-all"
                >
                  Go to My Dashboard
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </section>
            )}
          </div>
        )}

        {/* Video Library Tab */}
        {activeTab === 'videos' && (
          <div className="text-center py-20">
            <Film className="w-16 h-16 text-stone-700 mx-auto mb-6" />
            <h2 className="text-3xl font-light text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Video Library Coming Soon
            </h2>
            <p className="text-stone-500 max-w-md mx-auto">
              We're curating a collection of video stories, teachings, and conversations about compassionate communication.
            </p>
          </div>
        )}

        {/* My Journey Tab */}
        {activeTab === 'personal' && userId && (
          <div className="text-center py-20">
            <Heart className="w-16 h-16 text-amber-500/50 mx-auto mb-6" />
            <h2 className="text-3xl font-light text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Your Personal LORE Library
            </h2>
            <p className="text-stone-500 max-w-md mx-auto mb-8">
              Your private collection of reflections, insights, and growth moments from your NVC journey.
            </p>
            <Button 
              onClick={() => navigate('/dashboard')}
              className="bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 text-white px-8 py-3 rounded-none"
            >
              View in Dashboard
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-stone-900 py-12 mt-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="text-center md:text-left">
              <p className="text-stone-600 text-xs tracking-[0.2em] uppercase mb-1">The Belonging Sessions</p>
              <p className="text-stone-700 text-sm">Building Compassionate Connection • Defiance Springs</p>
            </div>
            <div className="flex items-center gap-8 text-sm">
              <button onClick={() => navigate('/')} className="text-stone-600 hover:text-amber-500 transition-colors">Home</button>
              <button onClick={() => navigate('/belonging-ebook')} className="text-stone-600 hover:text-amber-500 transition-colors">eBook</button>
              <button onClick={() => navigate('/ofnr-trainer')} className="text-stone-600 hover:text-amber-500 transition-colors">OFNR</button>
            </div>
          </div>
          <div className="text-center mt-8 pt-8 border-t border-stone-900">
            <p className="text-stone-700 text-xs">
              © 2025 The Belonging Sessions • Stories of Connection
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
